type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  level: LogLevel;
  enabled: boolean;
}

class Logger {
  private config: LoggerConfig;
  private levels: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3
  };

  // CSS стили для разных уровней логирования
  private styles: Record<LogLevel, string> = {
    debug: 'color: #0099cc; font-weight: normal;', // Голубой
    info: 'color: #00cc00; font-weight: normal;',  // Зеленый
    warn: 'color: #ff9900; font-weight: bold;',    // Оранжевый
    error: 'color: #ff0000; font-weight: bold;'    // Красный
  };

  constructor(config: LoggerConfig) {
    this.config = config;
  }

  private shouldLog(level: LogLevel): boolean {
    if (!this.config.enabled) return false;
    return this.levels[level] >= this.levels[this.config.level];
  }

  private formatMessage(level: LogLevel, context: string, message: string, ...args: any[]): void {
    if (!this.shouldLog(level)) return;

    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}] [${context}]`;
    const style = this.styles[level];

    switch (level) {
      case 'debug':
        console.log(`%c${prefix} ${message}`, style, ...args);
        break;
      case 'info':
        console.info(`%c${prefix} ${message}`, style, ...args);
        break;
      case 'warn':
        console.warn(`%c${prefix} ${message}`, style, ...args);
        break;
      case 'error':
        console.error(`%c${prefix} ${message}`, style, ...args);
        break;
    }
  }

  debug(context: string, message: string, ...args: any[]): void {
    this.formatMessage('debug', context, message, ...args);
  }

  info(context: string, message: string, ...args: any[]): void {
    this.formatMessage('info', context, message, ...args);
  }

  warn(context: string, message: string, ...args: any[]): void {
    this.formatMessage('warn', context, message, ...args);
  }

  error(context: string, message: string, ...args: any[]): void {
    this.formatMessage('error', context, message, ...args);
  }

  setLevel(level: LogLevel): void {
    this.config.level = level;
  }

  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
  }
}

// Создаем глобальный экземпляр логгера
const logger = new Logger({
  level: process.env.NODE_ENV === 'production' ? 'warn' : 'debug',
  enabled: true
});

export default logger;
